"use client";

import { useState } from "react";
import { API_BASE } from "@/lib/api";
import { track } from "@/lib/analytics";

type Size = "lg" | "sm";

type WaitlistEmailFieldProps = {
  size?: Size;
  source?: string;
};

export function WaitlistEmailField({ size = "lg", source = "hero" }: WaitlistEmailFieldProps) {
  const [value, setValue] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const inputFont = size === "lg" ? 19 : 16;
  const pillH = size === "lg" ? 44 : 38;
  const pillPad = size === "lg" ? "0 22px" : "0 18px";
  const pillFs = size === "lg" ? 14 : 13;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = value.trim();
    if (!email || status === "submitting") return;

    setStatus("submitting");
    setErrorMsg(null);

    track("tr_mfm_waitlist_submit_attempt", { source });

    if (website.trim().length > 0) {
      setStatus("done");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/v1/waitlist`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          source,
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 512) : undefined,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
      if (!res.ok || body.ok === false) {
        const message = body.message ?? "Something went wrong. Try again in a moment.";
        setStatus("error");
        setErrorMsg(message);
        track("tr_mfm_waitlist_submit_error", {
          source,
          status: res.status,
          reason: message,
        });
        return;
      }
      setStatus("done");
      track("tr_mfm_waitlist_submit_success", { source });
    } catch {
      setStatus("error");
      const message = "Something went wrong. Try again in a moment.";
      setErrorMsg(message);
      track("tr_mfm_waitlist_submit_error", {
        source,
        reason: "network_error",
      });
    }
  }

  if (status === "done") {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          fontFamily: "var(--display)",
          fontStyle: "italic",
          fontSize: size === "lg" ? 22 : 18,
          color: "var(--accent)",
          padding: "12px 0",
          borderBottom: "0.5px solid var(--hairline-strong)",
        }}
      >
        We&apos;ve got you. We&apos;ll write once.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%" }} noValidate>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          borderBottom: "0.5px solid var(--text)",
          padding: "10px 0 12px",
        }}
      >
        <label
          aria-hidden="true"
          style={{
            position: "absolute",
            left: -10000,
            top: "auto",
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
        >
          Website (leave empty)
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
        <input
          type="email"
          required
          aria-label="Email address"
          autoComplete="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="your email."
          disabled={status === "submitting"}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            padding: "4px 0",
            fontFamily: "var(--ui)",
            fontSize: inputFont,
            color: "var(--text)",
            letterSpacing: "-0.005em",
          }}
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          style={{
            height: pillH,
            padding: pillPad,
            borderRadius: 999,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            border: "none",
            cursor: status === "submitting" ? "default" : "pointer",
            fontFamily: "var(--ui)",
            fontSize: pillFs,
            fontWeight: 500,
            letterSpacing: "0.005em",
            whiteSpace: "nowrap",
            transition: "background 160ms ease, opacity 160ms ease",
            opacity: status === "submitting" ? 0.7 : 1,
          }}
        >
          {status === "submitting" ? "Joining…" : "Join the waitlist."}
        </button>
      </div>
      <div
        style={{
          marginTop: 10,
          fontFamily: "var(--ui)",
          fontSize: 12,
          color: status === "error" ? "var(--accent)" : "var(--text-3)",
          letterSpacing: "0.005em",
        }}
      >
        {status === "error" && errorMsg
          ? errorMsg
          : "We'll write once when we launch. Nothing else."}
      </div>
    </form>
  );
}
